import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  Optional,
  QueryList,
  Self,
} from '@angular/core';
import { FormControl, NgControl, ValidationErrors } from '@angular/forms';
import { Loading, TemplateDirective } from '@fleet-beat/core';
import { untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { SimpleField } from '../../abstracts/simple-field';
import { AnexConfig, AnexType, IconAnexConfig } from '../../models/anex-config.model';

@Component({
  selector: 'fb-phone-field',
  templateUrl: './phone-field.component.html',
  styleUrls: ['./phone-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PhoneFieldComponent extends SimpleField<string> implements OnInit {
  @Input() prefix: AnexConfig;
  @Input() noLabel: boolean;
  @Input() suffix: AnexConfig;
  @Input() templates: QueryList<TemplateDirective<object>>;
  @Input() loading: Loading = new Loading();

  phoneControl: FormControl = new FormControl();
  phonePrefix: string = '1';
  clearBtn: IconAnexConfig = { type: AnexType.Icon, iconName: 'clear', outlined: false };

  constructor(
    protected override changeDetectorRef: ChangeDetectorRef,
    @Optional()
    @Self()
    protected override ngControl: NgControl,
  ) {
    super(changeDetectorRef, ngControl);
  }

  ngOnInit(): void {
    this.initListeners();
  }

  onClear(): void {
    this.phoneControl.setValue(undefined);
  }

  protected override onWriteValue(value: string): void {
    if (!this.isEmpty(value)) {
      this.phoneControl.setValue(this.valueToInput(value));
    } else {
      this.phoneControl.setValue(undefined);
    }
  }

  private initListeners(): void {
    const control: FormControl = this.ngControl.control as FormControl;
    control.statusChanges.pipe(untilComponentDestroyed(this)).subscribe(() => {
      const errors: ValidationErrors = { ...this.phoneControl.errors, ...control.errors };
      if (!errors && Object.keys(errors).length > 0) {
        this.phoneControl.setErrors(errors);
      }
    });

    this.phoneControl.valueChanges
      .pipe(
        untilComponentDestroyed(this),
        map((value: string) => this.valueToInput(value)),
        map((value: string) => this.valueToOutput(value)),
        map((value: string) => (this.isEmpty(value) ? undefined : value)),
        distinctUntilChanged(),
      )
      .subscribe((value: string) => {
        this.value = value;
      });
  }

  private isEmpty(value: number | string): boolean {
    return value === undefined || value === null || value === '' || value === this.phonePrefix || Number.isNaN(value);
  }

  private valueToOutput(phone: string): string {
    if (!phone) {
      return phone;
    }
    return phone.startsWith(this.phonePrefix) && phone.length >= 11 ? phone : `${this.phonePrefix}${phone}`;
  }

  private valueToInput(phone: string): string {
    if (!phone) {
      return phone;
    }

    const result: string = phone.replace(/[^\d]+/g, '');
    return result.startsWith(this.phonePrefix) && phone.length >= 11
      ? result.substring(this.phonePrefix.length, result.length)
      : result;
  }
}
